// src/lib/api.ts
import { getFreshIdToken } from './firebase'; // you already have this

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

async function buildApiRequest(path: string, options: RequestInit = {}) {
  const token = await getFreshIdToken();

  const headers = new Headers(options.headers as HeadersInit | undefined);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const hasBody =
    typeof options.body !== 'undefined' && !(options.body instanceof FormData);

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  headers.set('Cache-Control', 'no-cache, no-store, max-age=0, must-revalidate');
  headers.set('Pragma', 'no-cache');

  const isGet = !options.method || options.method.toUpperCase() === 'GET';
  const needsBust = path.startsWith('/api/billing/subscription') || path.startsWith('/api/billing/prices');
  const sep = path.includes('?') ? '&' : '?';
  const url = `${API_BASE}${path}${isGet && needsBust ? `${sep}_ts=${Date.now()}` : ''}`;

  return {
    url,
    init: {
      ...options,
      headers,
      cache: 'no-store' as RequestCache,
    },
  };
}

async function parseApiError(res: Response): Promise<Error> {
  const contentType = res.headers.get('content-type') || '';
  let data: any = null;

  try {
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      const text = await res.text();
      try { data = text ? JSON.parse(text) : null; }
      catch { data = text; }
    }
  } catch {
    data = null;
  }

  const extractMessage = (value: any): string => {
    if (Array.isArray(value)) {
      return value.map((item) => extractMessage(item)).filter(Boolean).join(', ');
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    if (value && typeof value === 'object') {
      return extractMessage(value.message || value.error || value.detail || '');
    }
    return '';
  };

  const msg =
    extractMessage(data?.message) ||
    extractMessage(data?.error) ||
    (typeof data === 'string' ? data : '') ||
    res.statusText ||
    'Request failed';
  return new Error(`${res.status} ${msg}`.trim());
}

function parseApiErrorFromData(res: Response, data: any): Error {
  const extractMessage = (value: any): string => {
    if (Array.isArray(value)) {
      return value.map((item) => extractMessage(item)).filter(Boolean).join(', ');
    }
    if (typeof value === 'string') {
      return value.trim();
    }
    if (value && typeof value === 'object') {
      return extractMessage(value.message || value.error || value.detail || '');
    }
    return '';
  };

  const msg =
    extractMessage(data?.message) ||
    extractMessage(data?.error) ||
    (typeof data === 'string' ? data : '') ||
    res.statusText ||
    'Request failed';
  return new Error(`${res.status} ${msg}`.trim());
}

export async function apiFetch<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const { url, init } = await buildApiRequest(path, options);
  const res = await fetch(url, init);

  const contentType = res.headers.get('content-type') || '';
  let data: any = null;

  try {
    if (contentType.includes('application/json')) {
      data = await res.json();
    } else {
      const text = await res.text();
      try { data = text ? JSON.parse(text) : null; }
      catch { data = text; }
    }
  } catch {
    data = null;
  }

  if (!res.ok) {
    throw parseApiErrorFromData(res, data);
  }

  return (data ?? {}) as T;
}

export async function apiFetchStream(
  path: string,
  options: RequestInit = {},
): Promise<Response> {
  const { url, init } = await buildApiRequest(path, options);
  const res = await fetch(url, init);
  if (!res.ok) {
    throw await parseApiError(res);
  }
  return res;
}

